// Account widget — a small top-right overlay that shows who you're playing as
// and exposes Switch Character + Logout without hunting through the HUD.
// Grammar-gated: visible only during phase=playing.

import type { Net } from "../net.js";
import type { ClientStore, ClientState } from "../state.js";
import { getSettings, onSettings } from "./settings.js";

export function mountAccountWidget(_uiHost: HTMLElement, net: Net, store: ClientStore): void {
  const widget = document.createElement("div");
  widget.className = "account-widget";
  widget.id = "account-widget";
  widget.style.display = "none";
  document.body.appendChild(widget);

  function render(s: ClientState): void {
    // Grammar gate — only while playing + if settings allow.
    const cfg = getSettings();
    widget.style.display = s.phase === "playing" && cfg.show_account_widget ? "" : "none";
    if (s.phase !== "playing" || !cfg.show_account_widget) return;
    const me = s.characters.find((c) => c.id === s.character_id) ?? s.characters[0];
    const tier = s.entitlements.tier;
    const tierLabel = tier === "patron" ? "★ Patron" : tier === "adventurer" ? "✦ Adventurer" : "Free";
    const title = s.title ? ` <span class="acc-title">"${escape(s.title)}"</span>` : "";
    const guild = s.guild ? `<span class="acc-guild" title="Rank: ${escape(s.guild.rank)}">⚑ ${escape(s.guild.name)}</span>` : "";
    const tokens = s.tokens.balance > 0 || s.tokens.lifetime_earned > 0
      ? `<span class="acc-tokens" title="Hammer Tokens — type /tokens for details">◈ ${s.tokens.balance}</span>` : "";
    const moon = `<span class="acc-moon" title="Moon phase: ${escape(s.moon.phase)} — next in ${s.moon.seconds_until_next}s">${escape(s.moon.glyph)} ${escape(s.moon.phase)}</span>`;
    widget.innerHTML = `
      <div class="acc-summary">
        <span class="acc-name">${escape(me?.name ?? "…")}${title}</span>
        <span class="acc-meta">${escape(me?.faction ?? "")} · ${escape(me?.class ?? "")} · lvl ${me?.level ?? 1}</span>
        <span class="acc-tier acc-tier-${tier}">${tierLabel}</span>
        ${guild}
        ${tokens}
        ${moon}
      </div>
      <div class="acc-actions">
        <button class="acc-btn" id="acc-switch" title="Switch Character">⇄</button>
        <button class="acc-btn danger" id="acc-logout" title="Log Out">⎋</button>
      </div>
    `;
    widget.querySelector<HTMLButtonElement>("#acc-switch")?.addEventListener("click", () => {
      net.send({ t: "switch_character" });
    });
    widget.querySelector<HTMLButtonElement>("#acc-logout")?.addEventListener("click", () => {
      if (!confirm("Log out of your account?")) return;
      net.send({ t: "logout" });
      store.reset();
    });
  }

  store.subscribe(render);
  onSettings(() => render(store.get()));
  render(store.get());
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
